import React, { useState, useEffect, useCallback } from 'react';
import { MCPTool, MCPServer } from '@/shared/types';
import { mcpApi } from '../services/mcpApi';

interface InternalTool {
  id: string;
  name: string;
  description: string;
  category: 'internal' | 'mcp';
  enabled: boolean;
  serverId?: string;
  serverName?: string;
}

interface ToolSelectorProps {
  onToolsChange?: (enabledTools: string[]) => void;
  className?: string;
}

export const ToolSelector: React.FC<ToolSelectorProps> = ({
  onToolsChange,
  className = "",
}) => {
  const [internalTools, setInternalTools] = useState<InternalTool[]>([]);
  const [mcpTools, setMcpTools] = useState<MCPTool[]>([]);
  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [enabledTools, setEnabledTools] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'internal' | 'mcp'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  // Load tools and servers on mount
  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load internal tools
      const internalToolsList: InternalTool[] = [
        {
          id: 'send_to_chat',
          name: 'Send to Chat',
          description: 'Send a message to the chat interface',
          category: 'internal',
          enabled: true,
        },
        {
          id: 'create_artifact',
          name: 'Create Artifact',
          description: 'Create a markdown or React artifact',
          category: 'internal',
          enabled: true,
        },
        {
          id: 'emit_status_update',
          name: 'Status Update',
          description: 'Emit a status update event',
          category: 'internal',
          enabled: true,
        },
        {
          id: 'request_agent_spawn',
          name: 'Spawn Agent',
          description: 'Request spawning of a new agent',
          category: 'internal',
          enabled: true,
        },
        {
          id: 'analyze_task_complexity',
          name: 'Analyze Task',
          description: 'Analyze task complexity',
          category: 'internal',
          enabled: false,
        },
        {
          id: 'create_workflow_plan',
          name: 'Create Plan',
          description: 'Create a workflow execution plan',
          category: 'internal',
          enabled: false,
        },
        {
          id: 'search_information',
          name: 'Search Info',
          description: 'Search for information',
          category: 'internal',
          enabled: false,
        },
        {
          id: 'validate_information',
          name: 'Validate Info',
          description: 'Validate information',
          category: 'internal',
          enabled: false,
        },
      ];
      setInternalTools(internalToolsList);

      // Load MCP servers and tools from the API
      let mcpResponse: any = { success: false, data: [] };
      try {
        mcpResponse = await mcpApi.getServers();
        if (mcpResponse.success && mcpResponse.data) {
          setMcpServers(mcpResponse.data);
        } else {
          setMcpServers([]);
        }
      } catch (mcpError) {
        console.error('Failed to load MCP servers:', mcpError);
        setMcpServers([]);
      }

      // Convert MCP capabilities to tools
      const mcpToolsList: MCPTool[] = [];
      if (mcpResponse.success && mcpResponse.data) {
        mcpResponse.data.forEach((server: MCPServer) => {
          server.capabilities.forEach((capability: any) => {
            if (capability.type === 'tool') {
              mcpToolsList.push({
                id: `${server.id}:${capability.name}`,
                serverId: server.id,
                name: capability.name,
                description: capability.description,
                enabled: capability.enabled && server.enabled,
                parameters: capability.parameters || [],
                usageCount: 0,
              });
            }
          });
        });
      }
      setMcpTools(mcpToolsList);

      // Initialize enabled tools
      const initialEnabled = new Set([
        'send_to_chat',
        'create_artifact',
        'emit_status_update',
        'request_agent_spawn',
      ]);
      setEnabledTools(initialEnabled);
      onToolsChange?.(Array.from(initialEnabled));

    } catch (error) {
      console.error('Failed to load tools:', error);
    } finally {
      setLoading(false);
    }
  }, [onToolsChange]);

  const handleToolToggle = useCallback((toolId: string, enabled: boolean) => {
    setEnabledTools(prev => {
      const newSet = new Set(prev);
      if (enabled) {
        newSet.add(toolId);
      } else {
        newSet.delete(toolId);
      }
      onToolsChange?.(Array.from(newSet));
      return newSet;
    });
  }, [onToolsChange]);

  const handleServerToggle = useCallback((serverId: string, enabled: boolean) => {
    setMcpServers(prev => prev.map(server => 
      server.id === serverId 
        ? { ...server, enabled, status: enabled ? 'connected' : 'disconnected' }
        : server
    ));
    
    // Update MCP tools based on server status
    setMcpTools(prev => prev.map(tool => 
      tool.serverId === serverId 
        ? { ...tool, enabled: enabled && tool.enabled }
        : tool
    ));
  }, []);

  const filteredTools = React.useMemo(() => {
    const allTools = [
      ...internalTools.map(tool => ({ ...tool, serverName: 'Internal' })),
      ...mcpTools.map(tool => ({
        id: tool.id,
        name: tool.name,
        description: tool.description,
        category: 'mcp' as const,
        enabled: tool.enabled,
        serverId: tool.serverId,
        serverName: mcpServers.find(s => s.id === tool.serverId)?.name || 'Unknown Server',
      }))
    ];

    let filtered = allTools;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(tool => tool.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tool => 
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query) ||
        tool.serverName?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [internalTools, mcpTools, mcpServers, selectedCategory, searchQuery]);

  const getToolStatus = (tool: InternalTool) => {
    if (tool.category === 'internal') {
      return enabledTools.has(tool.id);
    } else {
      const mcpTool = mcpTools.find(t => t.id === tool.id);
      return mcpTool ? mcpTool.enabled && enabledTools.has(tool.id) : false;
    }
  };

  if (loading) {
    return (
      <div className={`p-4 text-center text-gray-400 ${className}`}>
        <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
        Loading tools...
      </div>
    );
  }

  return (
    <div className={`bg-gray-900 text-white ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <h3 className="text-lg font-semibold mb-3">Available Tools</h3>
        
        {/* Search and Filters */}
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400"
          />
          
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1 rounded text-sm ${
                selectedCategory === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              All ({filteredTools.length})
            </button>
            <button
              onClick={() => setSelectedCategory('internal')}
              className={`px-3 py-1 rounded text-sm ${
                selectedCategory === 'internal' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Internal ({internalTools.length})
            </button>
            <button
              onClick={() => setSelectedCategory('mcp')}
              className={`px-3 py-1 rounded text-sm ${
                selectedCategory === 'mcp' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              MCP ({mcpTools.length})
            </button>
          </div>
        </div>
      </div>

      {/* Tools List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredTools.length === 0 ? (
          <div className="p-4 text-center text-gray-400">
            <div className="text-4xl mb-2">🔧</div>
            <p>No tools found</p>
          </div>
        ) : (
          <div className="space-y-2 p-4">
            {filteredTools.map((tool) => {
              const isEnabled = getToolStatus(tool);
              const isMcpTool = tool.category === 'mcp';
              const server = isMcpTool ? mcpServers.find(s => s.id === tool.serverId) : null;
              const serverEnabled = server ? server.enabled : true;

              return (
                <div
                  key={tool.id}
                  className={`p-3 rounded-lg border transition-colors ${
                    isEnabled 
                      ? 'bg-blue-600/20 border-blue-500/50' 
                      : 'bg-gray-800/50 border-gray-700 hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium truncate">{tool.name}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          tool.category === 'internal' 
                            ? 'bg-green-600 text-green-200' 
                            : 'bg-purple-600 text-purple-200'
                        }`}>
                          {tool.category}
                        </span>
                        {isMcpTool && (
                          <span className="text-xs text-gray-400">
                            via {tool.serverName}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mb-2">{tool.description}</p>
                      {isMcpTool && server && (
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            server.status === 'connected' 
                              ? 'bg-green-500' 
                              : server.status === 'error'
                              ? 'bg-red-500'
                              : 'bg-gray-500'
                          }`} />
                          <span className="text-xs text-gray-500">
                            {server.status} • {server.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 ml-3">
                      {isMcpTool && (
                        <button
                          onClick={() => handleServerToggle(tool.serverId!, !serverEnabled)}
                          disabled={!server}
                          className={`px-2 py-1 text-xs rounded ${
                            serverEnabled
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
                          } ${!server ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={server ? `Server ${serverEnabled ? 'enabled' : 'disabled'}` : 'Server not found'}
                        >
                          {serverEnabled ? 'ON' : 'OFF'}
                        </button>
                      )}
                      <button
                        onClick={() => handleToolToggle(tool.id, !isEnabled)}
                        disabled={isMcpTool && !serverEnabled}
                        className={`px-3 py-1 text-xs rounded transition-colors ${
                          isEnabled
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
                        } ${isMcpTool && !serverEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={isMcpTool && !serverEnabled ? 'Enable server first' : `Tool ${isEnabled ? 'enabled' : 'disabled'}`}
                      >
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="p-4 border-t border-gray-700 bg-gray-800/50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-300">
            {enabledTools.size} of {filteredTools.length} tools enabled
          </span>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span>Internal: {internalTools.filter(t => enabledTools.has(t.id)).length}</span>
            <span>MCP: {mcpTools.filter(t => enabledTools.has(t.id)).length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
